import { useEffect, useRef, useState } from 'react';

import { realtime } from './client';

/**
 * Кто сейчас слушает канал: workspace:{id} — кто онлайн, task:{id} — кто
 * смотрит задачу. Сервер присылает полный список при каждой перемене,
 * хранить и сводить ничего не нужно.
 */
export function usePresence(channel: string | undefined): string[] {
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    if (channel === undefined || channel === '') {
      return;
    }

    const unsubscribe = realtime.subscribe(channel, (event) => {
      if (event.event === 'presence') {
        const list = event.data['user_ids'];

        setIds(Array.isArray(list) ? list.filter((v): v is string => typeof v === 'string') : []);
      }
    });

    return () => {
      unsubscribe();
      setIds([]);
    };
  }, [channel]);

  return ids;
}

/** Кто печатает в канале: id → пока не прошло `ttl` мс с последнего сигнала. */
export function useTyping(channel: string | undefined, ttl = 3000): string[] {
  const [typing, setTyping] = useState<string[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    if (channel === undefined || channel === '') {
      return;
    }

    const active = timers.current;

    const unsubscribe = realtime.subscribe(channel, (event) => {
      if (event.event !== 'typing') {
        return;
      }

      const id = event.data['user_id'];

      if (typeof id !== 'string') {
        return;
      }

      const existing = active.get(id);

      if (existing !== undefined) {
        clearTimeout(existing);
      }

      active.set(
        id,
        setTimeout(() => {
          active.delete(id);
          setTyping((list) => list.filter((v) => v !== id));
        }, ttl),
      );

      setTyping((list) => (list.includes(id) ? list : [...list, id]));
    });

    return () => {
      unsubscribe();

      for (const timer of active.values()) {
        clearTimeout(timer);
      }

      active.clear();
      setTyping([]);
    };
  }, [channel, ttl]);

  return typing;
}
